import { useState } from 'react'
import { admin } from '../api/client'
import type { AdminStation, Revision } from '../api/types'
import { useAsync } from '../lib/useAsync'
import { formatDateTime } from '../lib/time'
import { OUTCOMES, outcomeLabel, revisionSentence } from '../lib/revisions'

interface Props {
  stations: AdminStation[] | undefined
}

/** Readings the vendor sent that disagree with what is stored (db/016).
 *
 *  `measurement` holds one value per reading and the charts show it; these
 *  are the values it does not hold — the one a later vendor value replaced,
 *  a blank refused, the second of two readings for one time — and they
 *  exist nowhere else. Rare by design: about one per
 *  million readings in the 2026-09 audits. */
export function RevisedReadings({ stations }: Props) {
  const [station, setStation] = useState('')
  const [outcome, setOutcome] = useState<Revision['outcome'] | ''>('')
  const rows = useAsync(
    () => admin.revisions({ station: station || undefined,
                            outcome: outcome || undefined }),
    [station, outcome])

  const names = new Map(stations?.map((s) => [s.code, s.name]))

  return (
    <>
      <div className="section-head">
        <div className="panel-controls">
          <label className="field inline">
            <span>Station</span>
            <select value={station} onChange={(e) => setStation(e.target.value)}>
              <option value="">all</option>
              {stations?.map((s) => (
                <option key={s.code} value={s.code}>{s.name}</option>
              ))}
            </select>
          </label>
          <label className="field inline">
            <span>Kind</span>
            <select value={outcome}
                    onChange={(e) => setOutcome(e.target.value as Revision['outcome'] | '')}>
              <option value="">all</option>
              {OUTCOMES.map((o) => <option key={o} value={o}>{outcomeLabel(o)}</option>)}
            </select>
          </label>
        </div>
      </div>

      {rows.loading && <p className="muted">Loading…</p>}
      {rows.error && <p className="error">{rows.error}</p>}
      {rows.data?.length === 0 && (
        <p className="muted">No revised readings{station || outcome ? ' match' : ' yet'}.
          Every reading the vendors have sent agrees with what is stored.</p>
      )}
      {rows.data && rows.data.length > 0 && (
        <table className="grid">
          <thead>
            <tr>
              <th>Reading time</th><th>Station</th><th>Channel</th>
              <th>Kind</th><th>What happened</th><th>Seen</th>
            </tr>
          </thead>
          <tbody>
            {rows.data.map((r) => (
              <tr key={r.id}>
                <td className="nowrap">{formatDateTime(r.ts)}</td>
                <td className="nowrap">{names.get(r.station) ?? r.station}</td>
                <td>{r.display_name}{r.unit ? <small className="muted"> {r.unit}</small> : null}</td>
                <td className="nowrap">{outcomeLabel(r.outcome)}</td>
                <td className="revision-values">{revisionSentence(r)}</td>
                <td className="muted nowrap">{formatDateTime(r.seen_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}
